import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowLeftIcon,
  TagIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import MultiSelect from '../components/MultiSelect';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);

  const [requestTypes, setRequestTypes] = useState([]);
  const [loadingRequestTypes, setLoadingRequestTypes] = useState(true);
  const [showAddRequestTypeForm, setShowAddRequestTypeForm] = useState(false);
  const [newRequestTypeName, setNewRequestTypeName] = useState('');
  const [newRequestTypeCategoryIds, setNewRequestTypeCategoryIds] = useState([]);
  const [editingRequestTypeId, setEditingRequestTypeId] = useState(null);
  const [editingRequestTypeName, setEditingRequestTypeName] = useState('');
  const [editingRequestTypeCategoryIds, setEditingRequestTypeCategoryIds] = useState([]);
  const [savingRequestType, setSavingRequestType] = useState(false);

  useEffect(() => {
    loadCategories();
    loadRequestTypes();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getCategories();
      setCategories(data);
    } catch (err) {
      setError('Failed to load categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadRequestTypes = async () => {
    try {
      setLoadingRequestTypes(true);
      const data = await apiService.getRequestTypes();
      setRequestTypes(data);
    } catch (err) {
      console.error('Failed to load request types:', err);
    } finally {
      setLoadingRequestTypes(false);
    }
  };

  const categoryOptions = categories.map((c) => ({ value: c.id, label: c.name }));

  const handleAddRequestType = async (e) => {
    e.preventDefault();
    const name = newRequestTypeName.trim();
    if (!name) return;
    setSavingRequestType(true);
    try {
      await apiService.createRequestType({ name, categoryIds: newRequestTypeCategoryIds });
      setNewRequestTypeName('');
      setNewRequestTypeCategoryIds([]);
      setShowAddRequestTypeForm(false);
      await Promise.all([loadRequestTypes(), loadCategories()]);
    } catch (err) {
      alert('Failed to create request type. Please try again.');
    } finally {
      setSavingRequestType(false);
    }
  };

  const handleRequestTypeEditStart = (requestType) => {
    setEditingRequestTypeId(requestType.id);
    setEditingRequestTypeName(requestType.name);
    setEditingRequestTypeCategoryIds(requestType.categoryIds || []);
  };

  const handleRequestTypeEditSave = async (id) => {
    const name = editingRequestTypeName.trim();
    if (!name) return;
    setSavingRequestType(true);
    try {
      await apiService.updateRequestType(id, { name, categoryIds: editingRequestTypeCategoryIds });
      setEditingRequestTypeId(null);
      setEditingRequestTypeName('');
      setEditingRequestTypeCategoryIds([]);
      await Promise.all([loadRequestTypes(), loadCategories()]);
    } catch (err) {
      alert('Failed to update request type. Please try again.');
    } finally {
      setSavingRequestType(false);
    }
  };

  const handleRequestTypeEditCancel = () => {
    setEditingRequestTypeId(null);
    setEditingRequestTypeName('');
    setEditingRequestTypeCategoryIds([]);
  };

  const handleDeleteRequestType = async (requestType) => {
    if (!window.confirm(`Delete request type "${requestType.name}"? This cannot be undone.`)) return;
    try {
      await apiService.deleteRequestType(requestType.id);
      await Promise.all([loadRequestTypes(), loadCategories()]);
    } catch (err) {
      const msg = err?.message?.includes('409') || err?.status === 409
        ? `"${requestType.name}" has associated tickets and cannot be deleted.`
        : 'Failed to delete request type. It may have associated tickets.';
      alert(msg);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await apiService.createCategory({ name });
      setNewName('');
      setShowAddForm(false);
      await loadCategories();
    } catch (err) {
      alert('Failed to create category. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditStart = (category) => {
    setEditingId(category.id);
    setEditingName(category.name);
  };

  const handleEditSave = async (id) => {
    const name = editingName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await apiService.updateCategory(id, { name });
      setEditingId(null);
      setEditingName('');
      await loadCategories();
    } catch (err) {
      alert('Failed to update category. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Delete category "${category.name}"? This cannot be undone.`)) return;
    try {
      await apiService.deleteCategory(category.id);
      await loadCategories();
    } catch (err) {
      const msg = err?.message?.includes('409') || err?.status === 409
        ? `"${category.name}" has associated tickets and cannot be deleted.`
        : 'Failed to delete category. It may have associated tickets.';
      alert(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Link to="/settings" className="text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Category Management</h1>
          <p className="text-gray-600">Add, rename, or remove ticket categories</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <TagIcon className="h-5 w-5 text-gray-400" />
            <span className="font-medium text-gray-900">
              {categories.length} {categories.length === 1 ? 'category' : 'categories'}
            </span>
          </div>
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Add Category</span>
            </button>
          )}
        </div>

        {showAddForm && (
          <form onSubmit={handleAdd} className="px-6 py-4 border-b border-gray-200 bg-blue-50">
            <div className="flex items-center space-x-3">
              <input
                autoFocus
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Category name"
                maxLength={100}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={saving || !newName.trim()}
                className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <CheckIcon className="h-4 w-4" />
                <span>Save</span>
              </button>
              <button
                type="button"
                onClick={() => { setShowAddForm(false); setNewName(''); }}
                className="flex items-center space-x-1 px-3 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
                <span>Cancel</span>
              </button>
            </div>
          </form>
        )}

        {categories.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500 text-sm">
            No categories yet. Add one to get started.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {categories.map((category) => (
              <li key={category.id} className="flex items-center px-6 py-3">
                {editingId === category.id ? (
                  <div className="flex items-center space-x-3 flex-1">
                    <input
                      autoFocus
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      maxLength={100}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleEditSave(category.id);
                        if (e.key === 'Escape') handleEditCancel();
                      }}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => handleEditSave(category.id)}
                      disabled={saving || !editingName.trim()}
                      className="p-1.5 text-green-600 hover:text-green-800 disabled:opacity-50"
                      title="Save"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={handleEditCancel}
                      className="p-1.5 text-gray-400 hover:text-gray-600"
                      title="Cancel"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex-1">
                      <span className="text-sm text-gray-900">{category.name}</span>
                      {category.requestTypes?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {category.requestTypes.map((rt) => (
                            <span
                              key={rt.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                            >
                              {rt.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleEditStart(category)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Rename"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category)}
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-xs text-gray-400">
        Categories with existing tickets cannot be deleted. Rename them instead.
      </p>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <TagIcon className="h-5 w-5 text-gray-400" />
            <span className="font-medium text-gray-900">
              {requestTypes.length} {requestTypes.length === 1 ? 'request type' : 'request types'}
            </span>
          </div>
          {!showAddRequestTypeForm && (
            <button
              onClick={() => setShowAddRequestTypeForm(true)}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Add Request Type</span>
            </button>
          )}
        </div>

        {showAddRequestTypeForm && (
          <form onSubmit={handleAddRequestType} className="px-6 py-4 border-b border-gray-200 bg-blue-50 space-y-3">
            <div className="flex items-center space-x-3">
              <input
                autoFocus
                type="text"
                value={newRequestTypeName}
                onChange={(e) => setNewRequestTypeName(e.target.value)}
                placeholder="Request type name"
                maxLength={100}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={savingRequestType || !newRequestTypeName.trim()}
                className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <CheckIcon className="h-4 w-4" />
                <span>Save</span>
              </button>
              <button
                type="button"
                onClick={() => { setShowAddRequestTypeForm(false); setNewRequestTypeName(''); setNewRequestTypeCategoryIds([]); }}
                className="flex items-center space-x-1 px-3 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
                <span>Cancel</span>
              </button>
            </div>
            <MultiSelect
              options={categoryOptions}
              selectedValues={newRequestTypeCategoryIds}
              onChange={setNewRequestTypeCategoryIds}
              placeholder="Link to categories..."
              label="Categories"
            />
          </form>
        )}

        {loadingRequestTypes ? (
          <div className="px-6 py-12 text-center text-gray-500 text-sm">Loading...</div>
        ) : requestTypes.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500 text-sm">
            No request types yet. Add one and link it to the categories that should require it.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {requestTypes.map((requestType) => (
              <li key={requestType.id} className="px-6 py-3">
                {editingRequestTypeId === requestType.id ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <input
                        autoFocus
                        type="text"
                        value={editingRequestTypeName}
                        onChange={(e) => setEditingRequestTypeName(e.target.value)}
                        maxLength={100}
                        className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => handleRequestTypeEditSave(requestType.id)}
                        disabled={savingRequestType || !editingRequestTypeName.trim()}
                        className="p-1.5 text-green-600 hover:text-green-800 disabled:opacity-50"
                        title="Save"
                      >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={handleRequestTypeEditCancel}
                        className="p-1.5 text-gray-400 hover:text-gray-600"
                        title="Cancel"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <MultiSelect
                      options={categoryOptions}
                      selectedValues={editingRequestTypeCategoryIds}
                      onChange={setEditingRequestTypeCategoryIds}
                      placeholder="Link to categories..."
                      label="Categories"
                    />
                  </div>
                ) : (
                  <div className="flex items-center">
                    <div className="flex-1">
                      <span className="text-sm text-gray-900">{requestType.name}</span>
                      {requestType.categoryNames?.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {requestType.categoryNames.map((name) => (
                            <span
                              key={name}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 mt-1">Not linked to any category</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleRequestTypeEditStart(requestType)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRequestType(requestType)}
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-xs text-gray-400">
        Request types linked to a category become required when logging a ticket in that category. Categories with no linked request types skip the field entirely. Request types with existing tickets cannot be deleted.
      </p>
    </div>
  );
};

export default CategoryManagement;
